import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import LinksComponent from './Links';
import FindBranches from './FindBranches';
import Leadership from './Leadership';
import TodaysPrice from './TodaysPrice';
import { ChevronLeft, ChevronRight, Scale, Landmark, Users } from 'lucide-react';
import API_BASE_URL from '../services/apiconfig';

const IntroductionPage = () => {
  const { language } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [galleryItems, setGalleryItems] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [showFullText, setShowFullText] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    fetchGalleryData();
    fetchAnnouncements();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (videoRef.current) {
          if (entry.isIntersecting) {
            videoRef.current.play().catch(() => {});
          } else {
            videoRef.current.pause();
          }
        }
      },
      { threshold: 0.4 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [galleryItems, currentSlide]);

  // Reload + attempt play whenever the current slide changes to a video
  useEffect(() => {
    setVideoError(false);
    if (videoRef.current) {
      try {
        videoRef.current.load();
        const playPromise = videoRef.current.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } catch (e) {
        // ignore
      }
    }
  }, [currentSlide]);

  const fetchGalleryData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/gallery/list`);
      const data = await response.json();
      setGalleryItems(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching gallery data:', error);
      setLoading(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/announcement/list`);
      const data = await response.json();
      if (data.status === 'success') {
        setAnnouncements(data.data);
      }
      setAnnouncementsLoading(false);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setAnnouncementsLoading(false);
    }
  };

  const content = {
    en: {
      title: 'Introduction',
      mainText: 'Tamil Nadu has been a pioneer state in India\'s cooperative movement. The services provided by cooperative societies to people living in rural areas are noteworthy. Primarily, cooperative societies play a major role in increasing agricultural productivity and farmers\' income by providing crop loans at the appropriate time. These loans include interest-free loans for immediate repayment, short-term, medium-term, long-term loans, jewel loans, and produce pledge loans. Cooperative societies also implement the Crop Insurance Scheme.',
      fullText: 'Tamil Nadu has been a pioneer state in India\'s cooperative movement. The services provided by cooperative societies to people living in rural areas are noteworthy. Primarily, cooperative societies play a major role in increasing agricultural productivity and farmers\' income by providing crop loans at the appropriate time. These loans include interest-free loans for immediate repayment, short-term, medium-term, long-term loans, jewel loans, and produce pledge loans. Cooperative societies also implement the Crop Insurance Scheme. They manage the Public Distribution System by running fair price shops. Through the Market Intervention Scheme, they stabilize market prices by selling essential commodities such as tur dal, urad dal, chillies, tamarind, gingelly oil, and onions at reasonable prices through cooperative stores. Additionally, cooperative societies operate Farm Fresh cooperative stores that sell vegetables at lower prices and Amma Pharmacies that sell medicines at discounted prices.',
      readMore: 'Read more',
      readLess: 'Read less',
      whatsNewTitle: "What's New ?"
    },
    ta: {
      title: 'அறிமுகம்',
      mainText: 'இந்திய கூட்டுறவு இயக்கத்திற்கு தமிழ்நாடு முன்னோடி மாநிலமாக இருந்து வருகிறது. கிராமப்புறங்களில் வாழ்பவர்களுக்கு, கூட்டுறவுச் சங்கங்கள் வழங்கும் சேவை குறிப்பிடத்தக்கது. முக்கியமாக, குறித்த நேரத்தில் பயிர்க்கடன்கள் வழங்குவதன்மூலம் வேளாண் உற்பத்தித் திறனையும், உழவர் பெருமக்களின் வருவாயையும் அதிகரிப்பதில் கூட்டுறவுச் சங்கங்கள் முக்கிய பங்காற்றி வருகின்றன. உடனடியாகத் திருப்பிச் செலுத்துவதற்கான வட்டியில்லாக் கடன்கள், குறுகிய கால, இடைக்கால, நீண்டகாலக் கடன்கள், நகைக்கடன், உற்பத்திப்பொருள் அடமானக் கடன் ஆகியவையும் இக்கடன்களில் அடங்கும். மேலும், பயிர்க் காப்பீட்டுத் திட்டத்தையும் கூட்டுறவுச் சங்கங்கள் செயல்படுத்தி வருகின்றன.',
      fullText: 'இந்திய கூட்டுறவு இயக்கத்திற்கு தமிழ்நாடு முன்னோடி மாநிலமாக இருந்து வருகிறது. கிராமப்புறங்களில் வாழ்பவர்களுக்கு, கூட்டுறவுச் சங்கங்கள் வழங்கும் சேவை குறிப்பிடத்தக்கது. முக்கியமாக, குறித்த நேரத்தில் பயிர்க்கடன்கள் வழங்குவதன்மூலம் வேளாண் உற்பத்தித் திறனையும், உழவர் பெருமக்களின் வருவாயையும் அதிகரிப்பதில் கூட்டுறவுச் சங்கங்கள் முக்கிய பங்காற்றி வருகின்றன. உடனடியாகத் திருப்பிச் செலுத்துவதற்கான வட்டியில்லாக் கடன்கள், குறுகிய கால, இடைக்கால, நீண்டகாலக் கடன்கள், நகைக்கடன், உற்பத்திப்பொருள் அடமானக் கடன் ஆகியவையும் இக்கடன்களில் அடங்கும். மேலும், பயிர்க் காப்பீட்டுத் திட்டத்தையும் கூட்டுறவுச் சங்கங்கள் செயல்படுத்தி வருகின்றன. நியாயவிலைக் கடைகளை நடத்துவதன் மூலமாக, பொது விநியோகத் திட்டத்தையும் நிர்வகித்து வருகின்றன. அத்தியாவசியப் பொருட்களான துவரம் பருப்பு, உளுத்தம் பருப்பு, மிளகாய், புளி, நல்லெண்ணெய், வெங்காயம் ஆகியவற்றை நியாயமான விலையில் கூட்டுறவுக் கடைகள் மூலம் விற்பனை செய்யும் சந்தைக் குறுக்கீட்டு நடவடிக்கையின் மூலம் வெளிச்சந்தையில் விலைவாசியை நிலைப்படுத்தி வருகின்றன. இதுமட்டுமின்றி, குறைந்த விலையில் காய்கறிகளை விற்பனை செய்யும் பண்ணைப் பசுமை கூட்டுறவுக் கடைகளையும், தள்ளுபடி விலையில் மருந்துகளை விற்பனை செய்யும் அம்மா மருந்தகங்களையும் கூட்டுறவுச் சங்கங்கள் நடத்தி வருகின்றன.',
      readMore: 'மேலும் படிக்க',
      readLess: 'குறைவாக படிக்க',
      whatsNewTitle: "புதிதாக என்ன ?"
    }
  };

  const t = content[language];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % galleryItems.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + galleryItems.length) % galleryItems.length);
  };

  const getMediaUrl = (mediaPath) => {
    return `https://kooturavu.tn.gov.in/${mediaPath}`;
  };

  const isVideo = (mediaPath) => {
    return mediaPath?.toLowerCase().endsWith('.mp4') ||
      mediaPath?.toLowerCase().endsWith('.webm') ||
      mediaPath?.toLowerCase().endsWith('.ogg');
  };

  const currentItem = galleryItems[currentSlide];

  return (
    <div className="bg-gray-50" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      {/* Title Section */}
      <section className="py-4 sm:py-6 bg-white border-b-2 border-gray-200">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-2 sm:mb-3">
              {t.title}
            </h1>
            <div className="w-16 sm:w-20 h-1 bg-gray-800 mx-auto"></div>
            <div className="py-4 sm:py-6 px-3 sm:px-4 md:px-8 mt-3 sm:mt-4 bg-gray-50 border border-gray-200">
              <div className="max-w-6xl mx-auto">
                <p className="text-gray-700 leading-relaxed text-xs sm:text-sm md:text-base text-justify">
                  {showFullText ? t.fullText : t.mainText}
                  <button
                    onClick={() => setShowFullText(!showFullText)}
                    className="text-gray-800 hover:text-black ml-1 font-semibold transition-colors duration-200 underline"
                  >
                    {showFullText ? t.readLess : t.readMore}
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section
        ref={sectionRef}
        className="py-6 sm:py-8 overflow-hidden relative bg-gradient-to-r from-sky-100 to-sky-200"
      >
        <div className="absolute inset-0 z-0 opacity-3 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 text-gray-300 animate-pulse-slow">
            <Scale size={80} className="opacity-20" />
          </div>
          <div className="absolute bottom-1/4 right-1/4 text-gray-300 animate-float-slow">
            <Users size={100} className="opacity-20" />
          </div>
          <div className="absolute top-1/2 right-10 text-gray-300 animate-pulse-slow delay-1000">
            <Landmark size={60} className="opacity-20" />
          </div>
        </div>

        <div className="container mx-auto px-3 sm:px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 max-w-7xl mx-auto">

            {/* Image/Video Card */}
            <div className="bg-white shadow-lg overflow-hidden border border-gray-300">
              <div className="relative group">
                {loading ? (
                  <div className="w-full h-64 sm:h-80 flex items-center justify-center bg-gray-100">
                    <p className="text-gray-500">Loading...</p>
                  </div>
                ) : galleryItems.length > 0 && currentItem ? (
                  <>
                    {isVideo(currentItem.media_file_paths[0]) ? (
                      videoError ? (
                        <div className="w-full h-64 sm:h-80 flex items-center justify-center bg-gray-100">
                          <p className="text-gray-500 text-sm">Video could not be loaded</p>
                        </div>
                      ) : (
                        <video
                          key={getMediaUrl(currentItem.media_file_paths[0])}
                          ref={videoRef}
                          src={getMediaUrl(currentItem.media_file_paths[0])}
                          className="w-full h-64 sm:h-80 object-cover"
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="auto"
                          onError={() => setVideoError(true)}
                          onCanPlay={() => {
                            if (videoRef.current) {
                              videoRef.current.play().catch(() => {});
                            }
                          }}
                        />
                      )
                    ) : (
                      <img
                        src={getMediaUrl(currentItem.media_file_paths[0])}
                        alt={language === 'en' ? currentItem.title_eng : currentItem.title_tamil}
                        className="w-full h-64 sm:h-80 object-cover"
                      />
                    )}

                    {galleryItems.length > 1 && (
                      <>
                        <button
                          onClick={prevSlide}
                          className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-1.5 sm:p-2 shadow-md transition-all"
                        >
                          <ChevronLeft size={18} className="sm:w-5 sm:h-5" />
                        </button>
                        <button
                          onClick={nextSlide}
                          className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-1.5 sm:p-2 shadow-md transition-all"
                        >
                          <ChevronRight size={18} className="sm:w-5 sm:h-5" />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-64 sm:h-80 flex items-center justify-center bg-gray-100">
                    <p className="text-gray-500">No media available</p>
                  </div>
                )}
              </div>
            </div>

            {/* What's New Card */}
            <div className="bg-gray-50 shadow-lg p-4 sm:p-6 border border-gray-300 overflow-hidden">
              <div className="text-center mb-3 sm:mb-4">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {t.whatsNewTitle}
                </h3>
                <div className="w-16 sm:w-20 h-1 bg-gray-800 mx-auto mt-2"></div>
              </div>

              {(() => {
                const localItems = [
                  {
                    link: '/documents/rural-business-loan-5000-crore.pdf',
                    announcement_eng: '₹5,000 Crore Rural Business Loan Target for Farmers',
                    announcement_tamil: 'ஊரக தொழில் கடன் விவசாயிகளுக்கு வழங்க ரூ.5,000 கோடி இலக்கு',
                    description_eng: "Press Release — Hon'ble Minister for Co-operation urges farmers to make use of this new scheme. ₹5,000 Crore has been set as the target for providing Rural Business Loans to farmers.",
                    description_tamil: 'செய்தி வெளியீடு — ஊரக தொழில் கடன் விவசாயிகளுக்கு வழங்க ரூ.5,000 கோடி இலக்கு. மாண்புமிகு கூட்டுறவுத் துறை அமைச்சர் இந்த புதிய திட்டத்தினை பயன்படுத்திட வேண்டுகோள்.',
                  },
                  {
                    link: '/documents/minister-inspection-16-10-2026.pdf',
                    announcement_eng: 'Minister Inspects Cooperative Society Services (16.10.2026)',
                    announcement_tamil: 'அமைச்சர் கூட்டுறவுச் சங்க சேவைகள் ஆய்வு (16.10.2026)',
                    description_eng: "On 16.10.2026, Hon'ble Minister for Co-operation Thiru V. Gandhiraj inspected various service divisions of the Cooperative Society under the Tamil Nadu Government's 2026–2027 initiatives.",
                    description_tamil: 'தமிழ்நாடு அரசின் 2026–2027ஆம் ஆண்டிற்கான, ஊரக் கூட்டுறவுச் சங்கத்தின் பல்வேறு சேவைகள் பிரிவுகளில் 16.10.2026 அன்று மாண்புமிகு கூட்டுறவுத்துறை அமைச்சர் திரு வ. காந்திராஜ் அவர்கள் ஆய்வு.',
                  },
                  {
                    link: '/crop-loan-waiver-2026',
                    announcement_eng: '2026 Crop Loan Waiver Scheme',
                    announcement_tamil: '2026 பயிர்க்கடன் தள்ளுபடித் திட்டம்',
                    description_eng: 'The Government of Tamil Nadu has released an amount of ₹6,024.5614 Crore towards 2026 Crop Loan Waiver Scheme to 12,83,539 eligible farmers.',
                    description_tamil: 'தமிழ்நாடு அரசால், 2026 பயிர்க்கடன் தள்ளுபடித் திட்டத்தின் கீழ் தகுதியான 12,83,539 விவசாயிகளுக்கு பயிர்க்கடன் தள்ளுபடித் தொகையாக ரூ.6,024.5614 கோடி விடுவிக்கப்பட்டுள்ளது.',
                  },
                  {
                    link: '/documents/jewel-loan-notification.pdf',
                    announcement_eng: 'Jewel Loan Scheme',
                    announcement_tamil: 'நகைக் கடன் திட்டம்',
                    description_eng: 'Jewel loans of up to ₹10,000 per gram will be provided by Cooperative institutions.',
                    description_tamil: 'கூட்டுறவு நிறுவனங்களில், கிராமிற்கு ரூ.10,000/- வரை நகைக் கடன்கள் வழங்கப்படும்.',
                  },
                  {
                    link: '/documents/seeds-fertilizers-notification.pdf',
                    announcement_eng: 'Seeds & Fertilizers Supply',
                    announcement_tamil: 'விதைகள் மற்றும் உரங்கள் விநியோகம்',
                    description_eng: 'Seeds and fertilizers will be supplied to farmers at discounted prices through all Primary Agricultural Cooperative Credit Societies (PACCS) in Tamil Nadu.',
                    description_tamil: 'தமிழ்நாட்டில் உள்ள அனைத்து தொடக்க வேளாண்மைக் கூட்டுறவுக் கடன் சங்கங்கள் மூலமாக விவசாயிகளுக்கு தள்ளுபடி விலையில் விதைகள், உரங்கள் வழங்கப்படும்.',
                  },
                  {
                    link: '/documents/onion-distribution-launch.pdf',
                    announcement_eng: 'Onion Distribution Launched (09.10.2026)',
                    announcement_tamil: 'வெங்காய விநியோகம் தொடக்கம் (09.10.2026)',
                    description_eng: "Hon'ble Minister for Co-operation Thiru V. Gandhiraj inspected 700 metric tonnes of onions received at Chennai, Thandaiyarpet Goods Railway Yard and launched the sale through Fair Price Shops.",
                    description_tamil: 'மாண்புமிகு கூட்டுறவுத்துறை அமைச்சர் திரு. வி. காந்திராஜ் அவர்கள் சென்னை, தண்டையார்பேட்டை, சரக்கு இரயில் முனையத்தில் 700 மெட்ரிக் டன் வெங்காயத்தினை பார்வையிட்டு, நியாயவிலைக் கடைகள் மூலம் விற்பனை செய்யும் பணியினை தொடங்கி வைத்தார்.',
                  },
                ];
                const combined = [...localItems, ...(announcements || [])];
                if (announcementsLoading && combined.length === 0) {
                  return <div className="text-center text-gray-500">Loading...</div>;
                }
                if (combined.length === 0) {
                  return <div className="text-center text-gray-500">No announcements available</div>;
                }
                return (
                  <div className="h-48 overflow-hidden relative">
                    <style>
                      {`
                        @keyframes scrollVertical {
                          0% { transform: translateY(0); }
                          100% { transform: translateY(-50%); }
                        }
                        .vertical-marquee {
                          animation: scrollVertical ${combined.length * 8}s linear infinite;
                        }
                        .vertical-marquee:hover {
                          animation-play-state: paused;
                        }
                      `}
                    </style>
                    <div className="vertical-marquee">
                      {[...combined, ...combined].map((announcement, index) => {
                        const title = language === 'en' ? announcement.announcement_eng : announcement.announcement_tamil;
                        const desc = language === 'en' ? announcement.description_eng : announcement.description_tamil;
                        const inner = (
                          <>
                            <h4 className="font-semibold text-gray-800 text-base sm:text-lg leading-snug">{title}</h4>
                            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{desc}</p>
                          </>
                        );
                        return (
                          <div key={index} className="space-y-2 sm:space-y-3 mb-6 pb-6 border-b border-gray-300 last:border-b-0">
                            {announcement.link ? (
                              <a href={announcement.link} target={announcement.link.startsWith('http') || announcement.link.endsWith('.pdf') ? '_blank' : '_self'} rel="noopener noreferrer" className="block hover:bg-gray-100 -mx-2 px-2 py-1 transition-colors">
                                {inner}
                              </a>
                            ) : inner}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* Today's Price Section */}
      <TodaysPrice />

      {/* Leadership Section */}
      <Leadership />

      {/* Map Section */}
      <FindBranches />

      {/* Links Section */}
      <LinksComponent />
    </div>
  );
};

export default IntroductionPage;